import { drawShoreBackdrop } from "./assets";
import { blitSprite, parseSprite, snap, type Palette } from "./blit";

/**
 * Tropical shore backdrop.
 * Prefers the authored PNG in /public/sprites/scene/; falls back to a
 * procedural layered pixel scene (sky bands, clouds, isle, foam, palms).
 */

const CLOUD = parseSprite([
  "..........OOOOOO............",
  ".......OOOCCCCCCOOO.........",
  ".....OOCCCCCCCCCCCCOO.......",
  "....OCCCCCCCCCCCCCCCCO......",
  "...OCCCCHHHCCCCCCCCCCCO.....",
  "...OCCCHHHHCCCCCCCCCCCO.....",
  "..OCCCCHHCCCCCCCCCCCCCCO....",
  "..OCCCCCCCCCCCCCCCCCCCCO....",
  "..OCCCCCCCCCCDDDDCCCCCCO....",
  "...OCCCCCCCDDDDDDCCCCCO.....",
  "....OOCCCCCDDDDDCCCCOO......",
  "......OOOOOOOOOOOOOO........",
]);

const PALM_TOP = parseSprite([
  "........F...............",
  ".......FfF.....F........",
  "......F.F.F...FfF.......",
  "...F.F...F.F.F.F.F......",
  "..FfF.....F.F...FfF.....",
  ".F.F.......A.......F.F..",
  "F.F........A........F.F.",
  "..F........A........F...",
  "...........A............",
  "...........A............",
]);

const PALM_TRUNK = parseSprite([
  "..Tt..",
  ".TttT.",
  "..Tt..",
  ".TTt..",
  "..Tt..",
  ".Ttt..",
  "..Tt..",
  ".TTt..",
]);

const BUSH = parseSprite([
  "......FF......",
  "....FFfFfF....",
  "...FfFFfFFf...",
  "..FFfFFFFFfF..",
  ".FfFFFFFFFFFf.",
  ".FfFFFAfFFFFf.",
  "..FfFFFFFFFf..",
  "...FfFFFFFf...",
  "....FFFFFF....",
]);

const ROCK = parseSprite([
  "..OOOO..",
  ".ODDDDO.",
  "ODDMMDDO",
  "ODMMMMDO",
  ".OMMMMO.",
  "..OOOO..",
]);

const SAIL = parseSprite([
  "..O...",
  ".OUO..",
  "OUUUO.",
  ".O.O..",
  "..O...",
  "..O...",
]);

const SCENE: Palette = {
  O: "#1a2830",
  C: "#f2e6c8",
  H: "#fff6d8",
  D: "#d8c49a",
  F: "#2f6b3a",
  f: "#1e4a28",
  A: "#3d8a4a",
  T: "#c9a06a",
  t: "#a87848",
  M: "#6a7a80",
  U: "#f4f0e8",
  W: "#1a6b74",
  w: "#3a9aa0",
  L: "#7ec8c0",
  G: "#b8e0d8",
  B: "#0e3a42",
  R: "#e8f4f0",
  S: "#ffe566",
  K: "#f2c8a8",
  P: "#1a1020",
  N: "#c04058",
  I: "#79d6cb",
  E: "#fff8e8",
  c: "#d4c08a",
  r: "#c44a40",
};

function rect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.fillRect(snap(x), snap(y), Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
}

function windDashes(
  ctx: CanvasRenderingContext2D,
  w: number,
  y0: number,
  y1: number,
  time: number,
): void {
  ctx.fillStyle = "#9ecfc8";
  for (let i = 0; i < 18; i++) {
    const y = y0 + ((i * 17 + Math.floor(time * 3)) % Math.max(1, y1 - y0));
    const x = (i * 37 + Math.floor(time * 8)) % (w + 40) - 20;
    const len = 6 + (i % 5) * 3;
    rect(ctx, x, y, len, 1, "#9ecfc8");
  }
}

function drawCloud(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
): void {
  blitSprite(ctx, CLOUD, SCENE, x, y, scale);
}

function drawPalm(
  ctx: CanvasRenderingContext2D,
  baseX: number,
  baseY: number,
  scale: number,
  lean = 0,
): void {
  // trunk segments
  for (let i = 0; i < 7; i++) {
    blitSprite(
      ctx,
      PALM_TRUNK,
      SCENE,
      baseX + lean * i,
      baseY - (i + 1) * 6 * scale,
      scale,
    );
  }
  blitSprite(
    ctx,
    PALM_TOP,
    SCENE,
    baseX - 9 * scale + lean * 7,
    baseY - 52 * scale,
    scale,
  );
}

/** Shared beach/dock geometry — keep in sync with Theater.beachBounds. */
export function shoreLayout(
  w: number,
  h: number,
): { shoreLeft: number; shoreRight: number; sandTop: number; sandBottom: number } {
  const sandTop = Math.floor(h * 0.58);
  const shoreRight = Math.floor(w * 0.52);
  return {
    shoreLeft: 18,
    shoreRight: shoreRight - 12,
    sandTop: sandTop + 8,
    sandBottom: h - 20,
  };
}

/** Full tropical beach backdrop for the theater buffer. */
export function drawTropicalShore(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  time: number,
): { shoreLeft: number; shoreRight: number; sandTop: number; sandBottom: number } {
  ctx.imageSmoothingEnabled = false;

  // High-quality PNG stage — same layout constants so critters/dragons align.
  if (drawShoreBackdrop(ctx, w, h)) {
    return shoreLayout(w, h);
  }

  // Sky bands (teal family like the reference)
  rect(ctx, 0, 0, w, h * 0.2, "#2f7f86");
  rect(ctx, 0, h * 0.2, w, h * 0.14, "#3a8f8c");
  rect(ctx, 0, h * 0.34, w, h * 0.1, "#4aa29c");
  rect(ctx, 0, h * 0.44, w, h * 0.06, "#5ab0a8");

  windDashes(ctx, w, 4, Math.floor(h * 0.36), time);

  // Chunkier clouds (layered for volume)
  drawCloud(ctx, Math.floor(w * 0.02), 2, 2);
  drawCloud(ctx, Math.floor(w * 0.28), 8, 1);
  drawCloud(ctx, Math.floor(w * 0.48), 0, 2);
  drawCloud(ctx, Math.floor(w * 0.78), 6, 1);

  // Distant island / cliffs with terraces
  const isleX = Math.floor(w * 0.52);
  const isleY = Math.floor(h * 0.36);
  rect(ctx, isleX - 4, isleY + 14, 86, 18, "#245832");
  rect(ctx, isleX, isleY + 6, 78, 20, "#2f6b3a");
  rect(ctx, isleX + 8, isleY - 2, 58, 16, "#3d8a4a");
  rect(ctx, isleX + 18, isleY - 12, 34, 14, "#2f6b3a");
  rect(ctx, isleX + 24, isleY - 18, 18, 10, "#245832");
  // cliff face + spray
  rect(ctx, isleX + 48, isleY + 8, 22, 24, "#6a7a80");
  rect(ctx, isleX + 52, isleY + 10, 5, 18, "#e8f4f0");
  rect(ctx, isleX + 58, isleY + 12, 3, 14, "#e8f4f0");
  rect(ctx, isleX + 46, isleY + 28, 26, 2, "#d0e8e4");

  // Tiny sailboat on horizon
  blitSprite(ctx, SAIL, SCENE, Math.floor(w * 0.18), isleY + 10, 1);

  // Ocean body — layered teal bands
  const waterTop = Math.floor(h * 0.48);
  rect(ctx, 0, waterTop, w, h - waterTop, "#1a6b74");
  rect(ctx, 0, waterTop + 10, w, h - waterTop - 10, "#17636c");
  rect(ctx, 0, waterTop + 22, w, h - waterTop - 22, "#13545c");
  // ripple lines
  for (let i = 0; i < 10; i++) {
    const y = waterTop + 3 + i * 4 + Math.round(Math.sin(time * 2 + i) * 1);
    const color = i % 3 === 0 ? "#4ab0b0" : i % 2 === 0 ? "#2a8088" : "#1f7078";
    rect(ctx, (i * 13) % 20, y, w, 1, color);
  }

  // Foam / surf near shore — scalloped layers like the reference
  const sandTop = Math.floor(h * 0.58);
  const shoreRight = Math.floor(w * 0.52);
  for (let layer = 0; layer < 4; layer++) {
    const y = sandTop - 6 + layer * 3;
    const pulse = Math.round(Math.sin(time * 2.4 + layer) * 2);
    for (let s = 0; s < 7; s++) {
      const cx = shoreRight - 22 - layer * 5 + s * 9 + pulse;
      const amp = 3 + ((s + layer) % 3);
      rect(ctx, cx - amp, y, amp * 2 + 1, 1, layer === 0 ? "#f7fcfa" : "#e8f4f0");
      rect(ctx, cx - amp + 1, y + 1, amp * 2 - 1, 1, "#d0e8e4");
    }
  }

  // Curved sandy beach (left & bottom-left) with dune banding
  for (let y = sandTop; y < h; y++) {
    const t = (y - sandTop) / Math.max(1, h - sandTop);
    const right = Math.floor(shoreRight + t * w * 0.18 - Math.sin(t * 3) * 6);
    const band =
      y % 7 === 0 ? "#c9a86e" : y % 3 === 0 ? "#d4b080" : "#e2c49a";
    rect(ctx, 0, y, right, 1, band);
    // wet sand edge + darker tide line
    rect(ctx, right - 4, y, 3, 1, "#b89868");
    rect(ctx, right - 1, y, 1, 1, "#8a7048");
  }

  // Soft dune highlight near grass line
  rect(ctx, 0, sandTop, shoreRight - 8, 1, "#ecd2a4");
  rect(ctx, 4, sandTop + 3, Math.floor(shoreRight * 0.55), 1, "#f0d8b0");

  // Shore rocks
  blitSprite(ctx, ROCK, SCENE, shoreRight - 18, sandTop + 4, 1);
  blitSprite(ctx, ROCK, SCENE, shoreRight - 8, sandTop + 10, 1);
  blitSprite(ctx, ROCK, SCENE, Math.floor(w * 0.12), h - 14, 1);

  // Palms framing — taller / fuller like the reference
  drawPalm(ctx, 4, h - 2, 2, 0);
  drawPalm(ctx, 22, h - 4, 1, 1);
  drawPalm(ctx, w - 40, h - 2, 2, -1);
  drawPalm(ctx, w - 22, h - 6, 1, -1);

  // Foreground bushes
  blitSprite(ctx, BUSH, SCENE, 2, h - 20, 2);
  blitSprite(ctx, BUSH, SCENE, 28, h - 14, 1);
  blitSprite(ctx, BUSH, SCENE, w - 48, h - 18, 2);
  blitSprite(ctx, BUSH, SCENE, w - 24, h - 12, 1);

  // Grass tufts along dune
  for (let i = 0; i < 10; i++) {
    const x = 8 + i * Math.floor(shoreRight / 11);
    const sway = Math.round(Math.sin(time * 2 + i) * 1);
    rect(ctx, x + sway, sandTop - 3, 1, 4, "#2f6b3a");
    rect(ctx, x + 1 + sway, sandTop - 5, 1, 5, "#3d8a4a");
    rect(ctx, x - 1 + sway, sandTop - 4, 1, 3, "#2f6b3a");
  }

  return shoreLayout(w, h);
}
